import { AccountSettingsDialog } from "@/components/ui/account-settings-dialog";
import { runtimeConfig } from "@/runtime-config";
import { useAppSelector } from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import { APP_VERSION } from "@/utils/env-constants";
import { getDraftViewRoute } from "@/utils/redirects";
import { ToolHeaderBar, ToolHeaderBarProps } from "@faro-lotv/flat-ui";
import { useAuthContext } from "@faro-lotv/gate-keepers";
import { selectDashboardUrl } from "@faro-lotv/project-source";
import { useMemo, useState, type JSX } from "react";
import { ProgressOverviewMenu } from "./progress-overview/progress-overview-menu";
import { ViewerHelpCenterMenu } from "./viewer-help-center-menu";

type SphereViewerToolHeaderBarProps = Pick<
  ToolHeaderBarProps,
  | "contentRight"
  | "disableNavigation"
  | "toolName"
  | "exitHref"
  | "onExit"
  | "exitText"
> & {
  projectId?: string | undefined;
};

/** @returns A header bar for a tool inside the Sphere Viewer. */
export function SphereViewerToolHeaderBar({
  contentRight,
  onExit: onExitProp,
  projectId,
  ...toolHeaderBarProps
}: SphereViewerToolHeaderBarProps): JSX.Element {
  // Only use the backend defined dashboard url so guest users of unlisted project
  // will not get redirected to the old HB dashboard by mistake
  // But note that getDraftViewRoute falls back to runtimeConfig.externalLinks.dashboardUrl
  // if dashboardUrl is undefined.
  const dashboardUrl = useAppSelector(selectDashboardUrl);
  const menuLinks = useMenuLinks(dashboardUrl);
  const currentUser = useAppSelector(selectCurrentUser);
  const { requestLogin, logout } = useAuthContext();

  const [isSettingPageOpen, setIsSettingPageOpen] = useState<boolean>(false);

  return (
    <>
      <ToolHeaderBar
        menuLinks={menuLinks}
        appVersion={APP_VERSION}
        userDisplayInfo={currentUser}
        onLogin={requestLogin}
        onLogout={logout}
        onExit={onExitProp ? () => onExitProp() : undefined}
        exitHref={projectId ? getDraftViewRoute(projectId) : undefined}
        onAccountAndSecurity={() => setIsSettingPageOpen(true)}
        contentRight={
          <>
            <ViewerHelpCenterMenu />
            <ProgressOverviewMenu />
            {contentRight}
          </>
        }
        {...toolHeaderBarProps}
      />

      {isSettingPageOpen && (
        <AccountSettingsDialog onClose={() => setIsSettingPageOpen(false)} />
      )}
    </>
  );
}

/**
 * @param dashboardUrl The URL to the Sphere XG dashboard.
 * @returns A list of links to show in the user menu.
 */
export function useMenuLinks(
  dashboardUrl?: string,
): ToolHeaderBarProps["menuLinks"] {
  return useMemo(() => {
    const externalLinks = {
      ...runtimeConfig.externalLinks,
      dashboardUrl,
    };

    const menuLinks: ToolHeaderBarProps["menuLinks"] = [];

    if (externalLinks.termsOfServiceUrl) {
      menuLinks.push({
        href: externalLinks.termsOfServiceUrl,
        text: "Terms of service",
      });
    }

    if (externalLinks.privacyPolicyUrl) {
      menuLinks.push({
        href: externalLinks.privacyPolicyUrl,
        text: "Privacy policy",
      });
    }

    if (externalLinks.imprintUrl) {
      menuLinks.push({
        href: externalLinks.imprintUrl,
        text: "Imprint",
      });
    }

    return menuLinks;
  }, [dashboardUrl]);
}
